/**
 * Catalogue des prestations proposées à la prise de rendez-vous.
 *
 * Chaque prestation porte une durée d'intervention et une marge. C'est leur
 * somme qui est réellement bloquée dans l'agenda : le client, lui, ne voit que
 * la durée d'intervention.
 *
 * L'occupation se compte en tranches de 15 minutes, indépendamment du pas de la
 * grille réglé dans Horaires — d'où le rappel affiché en tête de page.
 */
import { useState, useEffect, useCallback } from "react";
import { MainLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Wrench, Stethoscope, Sparkles, Plus, Pencil, Trash2, Loader2, Clock,
  EyeOff, Euro, Info, GripVertical, Users,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const TRANCHE = 15; // minutes — doit rester aligné sur ATOME_MINUTES côté serveur

const CATEGORIES = [
  { value: "reparation", label: "Réparation", icon: Wrench },
  { value: "diagnostic", label: "Diagnostic", icon: Stethoscope },
  { value: "service", label: "Service", icon: Sparkles },
];

const VIDE = {
  name: "",
  category: "reparation",
  duration_minutes: 30,
  buffer_minutes: 0,
  capacity: 1,
  price_from: "",
  description: "",
  device_required: true,
  active: true,
  hidden: false,
  code: "",
  order: 50,
};

const duree = (min) => {
  const h = Math.floor((min || 0) / 60), m = (min || 0) % 60;
  return h && m ? `${h} h ${m}` : h ? `${h} h` : `${m} min`;
};

export default function PrestationsPage() {
  const [items, setItems] = useState([]);
  const [grille, setGrille] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edition, setEdition] = useState(null);   // prestation en cours d'édition, ou null
  const [saving, setSaving] = useState(false);
  const [aSupprimer, setASupprimer] = useState(null);

  const charger = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/admin/prestations"),
        api.get("/admin/shop-settings").catch(() => null),
      ]);
      setItems(p.data || []);
      if (s) setGrille(s.data?.slot_minutes ?? null);
    } catch {
      toast.error("Impossible de charger le catalogue.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const ouvrir = (p) =>
    setEdition(p ? { ...VIDE, ...p, price_from: p.price_from ?? "", code: p.code ?? "" } : { ...VIDE });

  const enregistrer = async () => {
    const e = edition;
    if (!e.name.trim()) return toast.error("Le nom est obligatoire.");
    if (!e.duration_minutes || e.duration_minutes < 1) return toast.error("La durée doit être d'au moins 1 minute.");

    // Le serveur attend des types stricts : un champ laissé vide n'est pas « 0 ».
    const corps = {
      name: e.name.trim(),
      category: e.category,
      duration_minutes: Number(e.duration_minutes),
      buffer_minutes: Number(e.buffer_minutes) || 0,
      price_from: e.price_from === "" ? null : Number(e.price_from),
      description: e.description?.trim() || null,
      capacity: Math.max(1, Number(e.capacity) || 1),
      device_required: !!e.device_required,
      active: !!e.active,
      hidden: !!e.hidden,
      code: e.code?.trim() || null,
      order: Number(e.order) || 0,
    };

    setSaving(true);
    try {
      if (e.id) await api.put(`/admin/prestations/${e.id}`, corps);
      else await api.post("/admin/prestations", corps);
      toast.success(e.id ? "Prestation modifiée" : "Prestation ajoutée");
      setEdition(null);
      charger();
    } catch (err) {
      toast.error(err.response?.data?.detail || "L'enregistrement a échoué.");
    } finally {
      setSaving(false);
    }
  };

  const supprimer = async () => {
    try {
      await api.delete(`/admin/prestations/${aSupprimer.id}`);
      toast.success("Prestation supprimée");
      setItems((l) => l.filter((x) => x.id !== aSupprimer.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "La suppression a échoué.");
    } finally {
      setASupprimer(null);
    }
  };

  const basculerActif = async (p) => {
    try {
      await api.put(`/admin/prestations/${p.id}`, { ...p, active: !p.active });
      setItems((l) => l.map((x) => (x.id === p.id ? { ...x, active: !x.active } : x)));
    } catch {
      toast.error("Modification impossible.");
    }
  };

  return (
    <MainLayout>
      <div className="p-4 lg:p-6 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-chivo text-2xl font-bold">Prestations</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Ce que vos clients choisissent en prenant rendez-vous, et le temps que vous y consacrez.
            </p>
          </div>
          <Button onClick={() => ouvrir(null)}>
            <Plus className="mr-2 h-4 w-4" /> Ajouter
          </Button>
        </div>

        <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 text-sm">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p className="text-muted-foreground">
            Le <strong className="text-foreground">temps bloqué</strong> dans votre agenda est la somme de la
            durée et de la marge ; le client ne voit que la durée. L'occupation se compte par tranches de{" "}
            {TRANCHE} minutes, donc une prestation de 20 minutes en occupe 30.
            {grille != null && (
              <> Les heures de rendez-vous sont proposées toutes les {grille} minutes, réglable dans Horaires.</>
            )}
          </p>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-lg border border-border bg-card" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">
            Aucune prestation. Ajoutez-en une pour que vos clients puissent prendre rendez-vous.
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {items.map((p) => {
              const Icon = (CATEGORIES.find((c) => c.value === p.category) || CATEGORIES[0]).icon;
              const bloque = (p.duration_minutes || 0) + (p.buffer_minutes || 0);
              return (
                <div
                  key={p.id}
                  className={`flex items-start gap-3 rounded-lg border border-border bg-card p-4 transition-opacity ${p.active ? "" : "opacity-50"}`}
                >
                  <span className="hidden pt-1 text-muted-foreground/40 sm:block"><GripVertical className="h-4 w-4" /></span>
                  <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{p.name}</span>
                      {p.hidden && (
                        <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                          <EyeOff className="h-3 w-3" /> masquée
                        </span>
                      )}
                      {!p.device_required && (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                          appareil facultatif
                        </span>
                      )}
                    </div>
                    {p.description && (
                      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{p.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                      <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                        <Clock className="h-3 w-3" /> {duree(p.duration_minutes)}
                        {p.buffer_minutes ? ` + ${p.buffer_minutes} min de marge` : ""}
                      </span>
                      <span className="rounded bg-secondary px-1.5 py-0.5 text-muted-foreground">
                        bloque {duree(Math.ceil(bloque / TRANCHE) * TRANCHE)}
                      </span>
                      {(p.capacity || 1) > 1 && (
                        <span className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 font-medium text-primary">
                          <Users className="h-3 w-3" /> {p.capacity} clients à la fois
                        </span>
                      )}
                      {p.price_from != null && (
                        <span className="inline-flex items-center gap-1 rounded bg-accent/10 px-1.5 py-0.5 font-medium text-accent">
                          <Euro className="h-3 w-3" /> dès {p.price_from} €
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    <Switch
                      checked={!!p.active}
                      onCheckedChange={() => basculerActif(p)}
                      aria-label={p.active ? "Désactiver" : "Activer"}
                    />
                    <Button variant="ghost" size="icon" onClick={() => ouvrir(p)} aria-label="Modifier">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setASupprimer(p)} aria-label="Supprimer">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ------------------------------------------------ édition / création */}
      <Dialog open={!!edition} onOpenChange={(o) => !o && setEdition(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{edition?.id ? "Modifier la prestation" : "Nouvelle prestation"}</DialogTitle>
          </DialogHeader>

          {edition && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Nom *</Label>
                <Input
                  value={edition.name}
                  onChange={(e) => setEdition({ ...edition, name: e.target.value })}
                  placeholder="Changement d'écran"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea
                  rows={2}
                  value={edition.description || ""}
                  onChange={(e) => setEdition({ ...edition, description: e.target.value })}
                  placeholder="Remplacement de la vitre ou du bloc écran complet."
                />
                <p className="text-xs text-muted-foreground">Affichée sous le nom, sur le site.</p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Catégorie</Label>
                  <Select value={edition.category} onValueChange={(v) => setEdition({ ...edition, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Ordre d'affichage</Label>
                  <Input
                    type="number" inputMode="numeric" min={0}
                    value={edition.order}
                    onChange={(e) => setEdition({ ...edition, order: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">Du plus petit au plus grand.</p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Durée d'intervention (min) *</Label>
                  <Input
                    type="number" inputMode="numeric" min={1}
                    value={edition.duration_minutes}
                    onChange={(e) => setEdition({ ...edition, duration_minutes: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">Ce que voit le client.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Marge après (min)</Label>
                  <Input
                    type="number" inputMode="numeric" min={0}
                    value={edition.buffer_minutes}
                    onChange={(e) => setEdition({ ...edition, buffer_minutes: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">Rangement, nettoyage. Invisible pour le client.</p>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Clients en même temps</Label>
                <Input
                  type="number" inputMode="numeric" min={1}
                  value={edition.capacity}
                  onChange={(e) => setEdition({ ...edition, capacity: e.target.value })}
                />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  1 = le créneau est réservé à ce seul client, à l'établi. Au-delà, plusieurs
                  clients peuvent choisir la même heure — pour ce qui se règle au comptoir en
                  quelques minutes, comme une pose de film ou une récupération.
                  {" "}Les deux files sont indépendantes : une récupération ne bloque pas une
                  intervention d'atelier, et l'inverse est vrai aussi.
                </p>
              </div>

              <div className="rounded-md border border-border bg-secondary/40 p-3 text-sm">
                Temps réellement bloqué dans l'agenda :{" "}
                <strong>
                  {duree(Math.ceil(((Number(edition.duration_minutes) || 0) + (Number(edition.buffer_minutes) || 0)) / TRANCHE) * TRANCHE)}
                </strong>
                <span className="text-muted-foreground"> (arrondi à la tranche de {TRANCHE} min)</span>
              </div>

              <div className="space-y-1.5">
                <Label>Tarif indicatif (€)</Label>
                <Input
                  type="number" inputMode="decimal" min={0} step="0.01"
                  value={edition.price_from}
                  onChange={(e) => setEdition({ ...edition, price_from: e.target.value })}
                  placeholder="laisser vide pour ne rien afficher"
                />
                <p className="text-xs text-muted-foreground">Affiché « dès … € ». Vide = aucun prix annoncé.</p>
              </div>

              <div className="space-y-3 rounded-md border border-border p-3">
                <label className="flex items-start justify-between gap-3">
                  <span>
                    <span className="block text-sm font-medium">Demander l'appareil</span>
                    <span className="block text-xs text-muted-foreground">
                      Marque et modèle obligatoires à la prise de rendez-vous.
                    </span>
                  </span>
                  <Switch
                    checked={!!edition.device_required}
                    onCheckedChange={(v) => setEdition({ ...edition, device_required: v })}
                  />
                </label>

                <label className="flex items-start justify-between gap-3">
                  <span>
                    <span className="block text-sm font-medium">Proposée aux clients</span>
                    <span className="block text-xs text-muted-foreground">
                      Décochez pour la retirer du site sans la supprimer.
                    </span>
                  </span>
                  <Switch
                    checked={!!edition.active}
                    onCheckedChange={(v) => setEdition({ ...edition, active: v })}
                  />
                </label>

                <label className="flex items-start justify-between gap-3">
                  <span>
                    <span className="block text-sm font-medium">Masquer de la liste</span>
                    <span className="block text-xs text-muted-foreground">
                      Reste utilisable, mais n'apparaît pas dans le choix des prestations. C'est le cas de
                      la récupération de matériel, choisie par son propre bouton.
                    </span>
                  </span>
                  <Switch
                    checked={!!edition.hidden}
                    onCheckedChange={(v) => setEdition({ ...edition, hidden: v })}
                  />
                </label>
              </div>

              {edition.code ? (
                <p className="text-xs text-muted-foreground">
                  Repère technique : <code className="mono">{edition.code}</code> — utilisé par le site pour
                  sélectionner cette prestation automatiquement. Ne pas le modifier.
                </p>
              ) : null}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdition(null)}>Annuler</Button>
            <Button onClick={enregistrer} disabled={saving}>
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enregistrement…</> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------- suppression */}
      <AlertDialog open={!!aSupprimer} onOpenChange={(o) => !o && setASupprimer(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer « {aSupprimer?.name} » ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les rendez-vous déjà pris pour cette prestation gardent leur durée et ne sont pas touchés.
              Si vous voulez seulement cesser de la proposer, désactivez-la plutôt : elle restera lisible
              dans l'historique.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={supprimer}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
